import Handsontable from 'handsontable';

import { FarmdokColumnSettings, UserColumnSettings } from '@/shared/handsontable/rework/types';

export function toColumnsForPageSettings(columns: FarmdokColumnSettings[], hot: Handsontable): ColumnForPageSettings[] {
  const fixedColumnsLeft = hot.getSettings().fixedColumnsLeft ?? 0;

  return columns.map((column, physicalColumn) => {
    if (typeof column.data !== 'string') throw new Error('column.data is not a string');

    const prop = getColumnProp(column);
    const visualColumn = hot.propToCol(prop);

    return {
      ...column,
      physicalColumn,
      visualColumn,
      fixed: visualColumn < fixedColumnsLeft,
      visible: getVisible(visualColumn, hot),
      decimalPlaces: getDecimalPlaces(column, visualColumn, hot),
    };
  });
}

export function toUserColumnSettings(
  columnsForPageSettings: ColumnForPageSettings[],
): Record<string, UserColumnSettings> {
  return columnsForPageSettings.reduce((acc, column) => {
    if (typeof column.data !== 'string') throw new Error('column.data is not a string');
    return {
      ...acc,
      [column.data]: {
        visualColumn: column.visualColumn,
        physicalColumn: column.physicalColumn,
        hidden: !column.visible,
        ...(column.configurableDecimalPlaces && typeof column.decimalPlaces === 'number'
          ? { decimalPlaces: column.decimalPlaces }
          : {}),
      },
    };
  }, {} as Record<string, UserColumnSettings>);
}

function getColumnProp(column: Handsontable.ColumnSettings): string {
  if (typeof column.data !== 'string') throw new Error('column.data is not a string');
  if (column.type === 'subtable') return column.subtableColumns[0].data;
  return column.data;
}

/**
 * The user configured decimal places live in the Handsontable cell meta (persisted via the
 * persistentState plugin, see TableActivities.vue), not in the static column definitions. We read the
 * effective value from there so the settings modal shows what the table actually renders. Falls back to
 * the column default when nothing is configured or the table has no rows.
 */
function getDecimalPlaces(column: FarmdokColumnSettings, visualColumn: number, hot: Handsontable): number | undefined {
  if (hot.countRows() === 0 || visualColumn < 0) return column.decimalPlaces;
  const { decimalPlaces } = hot.getCellMeta(0, visualColumn) as { decimalPlaces?: unknown };
  return typeof decimalPlaces === 'number' ? decimalPlaces : column.decimalPlaces;
}

function getVisible(visualColumn: number, hot: Handsontable) {
  const plugin = hot.getPlugin('hiddenColumns');
  if (!plugin.isEnabled()) return true;

  const hiddenColumns = plugin.getHiddenColumns();
  return !hiddenColumns.includes(visualColumn);
}

export type ColumnForPageSettings = FarmdokColumnSettings & {
  physicalColumn: number;
  visualColumn: number;
  fixed: boolean;
  visible: boolean;
};
